import { renderRowsWithSpacing } from "@/lib/accordion-content";
import type {
  SculptureEditionHistoryEntry,
  SculptureEditionInAuction,
} from "@/lib/types";

type ProvenanceRow =
  | {
      kind: "history";
      index: number;
      item: SculptureEditionHistoryEntry;
    }
  | {
      kind: "auction";
      index: number;
      item: SculptureEditionInAuction;
    };

const cleanParts = (parts: Array<string | null | undefined>) =>
  parts.map((part) => part?.trim()).filter(Boolean) as string[];

const hasPersistedOrder = (rows: ProvenanceRow[]) =>
  rows.some((row) => (row.item.provenanceRank ?? 0) > 0);

const getFallbackOrder = (row: ProvenanceRow, historyCount: number) => {
  if (row.kind === "history") return row.item.sequence ?? row.index;
  return historyCount + row.index;
};

export function buildSculptureEditionProvenance({
  history,
  auctions,
  t,
}: {
  history?: SculptureEditionHistoryEntry[];
  auctions?: SculptureEditionInAuction[];
  t: (key: string) => string;
}) {
  const historyRows: ProvenanceRow[] = (history ?? []).map((item, index) => ({
    kind: "history",
    item,
    index,
  }));
  const auctionRows: ProvenanceRow[] = (auctions ?? []).map((item, index) => ({
    kind: "auction",
    item,
    index,
  }));
  const rows = [...historyRows, ...auctionRows];
  if (rows.length === 0) return null;

  const shouldUsePersistedOrder = hasPersistedOrder(rows);
  rows.sort((a, b) => {
    const aRank = shouldUsePersistedOrder
      ? (a.item.provenanceRank ?? 0)
      : getFallbackOrder(a, historyRows.length);
    const bRank = shouldUsePersistedOrder
      ? (b.item.provenanceRank ?? 0)
      : getFallbackOrder(b, historyRows.length);
    if (aRank !== bRank) return aRank - bRank;
    if (a.kind !== b.kind) return a.kind === "history" ? -1 : 1;
    return (a.item.id ?? "").localeCompare(b.item.id ?? "");
  });

  const renderedRows = rows
    .map((row) => {
      if (row.kind === "history") {
        const item = row.item;
        const location = item.collectionParticuliere
          ? t("computeField.collectionParticuliere")
          : item.description;
        return cleanParts([location, item.villePays, item.dates]).join(", ");
      }

      const item = row.item;
      const auction = item.auction;
      const location = cleanParts([
        auction?.ventesDescriptionCommissaires,
        auction?.ventesDescriptionLieu,
        auction?.ventesDescriptionVille,
        auction?.ventesDescriptionPays,
        item.sculptureEditionDansVentesNumeroLot
          ? `lot n°${item.sculptureEditionDansVentesNumeroLot}`
          : null,
      ]).join(", ");
      // Titre de la vente en tête quand la case « Inclure le titre dans le résumé » est cochée.
      return cleanParts([
        auction?.ventesDescriptionInclureTitreDansResume
          ? auction.ventesDescriptionTitre
          : null,
        cleanParts([auction?.ventesDescriptionDate, location]).join(" - "),
      ]).join(", ");
    })
    .filter(Boolean);

  return renderedRows.length > 0 ? renderRowsWithSpacing(renderedRows) : null;
}
